import React, { useState, useRef, useEffect } from "react";
import { Heart, Share2, ArrowLeft, ArrowRight } from "lucide-react";
import NotFoundScreen from "./NotFoundScreen";
import "./screens.css";

const AppScreen = ({
  app,
  goToHome,
  handleShare,
  handleLike,
  handleRemoveLike,
  isLoggedIn, // ログイン状態を受け取る
  triggerLogin, // ログイン処理をトリガーする関数
}) => {
  const [likes, setLikes] = useState(app?.likes || 0);
  const [hasLiked, setHasLiked] = useState(false);
  const iframeRef = useRef(null);

  // いいね状態を初期化
  useEffect(() => {
    if (!app) return;

    // ローカルストレージからいいね済みアプリのリストを取得
    const likedApps = JSON.parse(localStorage.getItem("likedApps") || "[]");
    if (likedApps.includes(app.id)) {
      setHasLiked(true);
    }
  }, [app]);

  // アプリデータのエラー処理の改善
  useEffect(() => {
    if (app?.content && iframeRef.current) {
      try {
        // Inject an error handler into the iframe content
        const iframeErrorHandlerScript = `
          <script>
            window.onerror = function(message, source, lineno, colno, error) {
              window.parent.postMessage({
                type: 'iframeError',
                payload: {
                  message: message,
                  source: source,
                  lineno: lineno,
                  colno: colno,
                  error: error ? error.stack : null
                }
              }, '*');
              return true; // Prevents the browser's default error handling
            };
          <\/script>
        `;
        const fullHtmlContent = iframeErrorHandlerScript + app.content;
        const blob = new Blob([fullHtmlContent], { type: "text/html" });
        const blobUrl = URL.createObjectURL(blob);

        iframeRef.current.src = blobUrl;

        return () => {
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (err) {
            console.error("Failed to revoke object URL:", err);
          }
        };
      } catch (err) {
        console.error("Error setting iframe content:", err);
      }
    }
  }, [app?.content]);

  // Listen for error messages from the iframe
  useEffect(() => {
    const handleIframeError = (event) => {
      if (
        event.source === iframeRef.current?.contentWindow &&
        event.data?.type === 'iframeError'
      ) {
        console.error("Error in iframe:", event.data.payload);
        alert("このWEBアプリは壊れている可能性があります。作り直してみる事をおすすめします。");
      }
    };

    window.addEventListener('message', handleIframeError);

    return () => {
      window.removeEventListener('message', handleIframeError);
    };
  }, []); // Empty dependency array ensures this runs once on mount and cleans up on unmount

  if (!app) {
    return <NotFoundScreen goToHome={goToHome} />;
  }

  const onLike = async () => {
    try {
      if (hasLiked) {
        // いいねを取り消す処理
        const newLikes = await handleRemoveLike(app.id);

        if (newLikes !== null) {
          // いいね数を更新
          setLikes(newLikes);

          // いいね済み状態を解除
          setHasLiked(false);

          // ローカルストレージからも削除
          const likedApps = JSON.parse(
            localStorage.getItem("likedApps") || "[]"
          );
          const updatedLikedApps = likedApps.filter((id) => id !== app.id);
          localStorage.setItem("likedApps", JSON.stringify(updatedLikedApps));
        }
      } else {
        // いいねを追加する処理
        const newLikes = await handleLike(app.id);

        if (newLikes !== null) {
          // いいね数を更新
          setLikes(newLikes);

          // いいね済み状態に設定
          setHasLiked(true);

          // ローカルストレージに保存
          const likedApps = JSON.parse(
            localStorage.getItem("likedApps") || "[]"
          );
          likedApps.push(app.id);
          localStorage.setItem("likedApps", JSON.stringify(likedApps));
        }
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  // 有料プランユーザーのアプリかどうか判定
  const isPaidPlan = app.creatorPlan === 'light' || app.creatorPlan === 'creator';

  return (
    <div className={`Appscreen app-screen-container ${isPaidPlan ? 'app-screen-fullscreen' : ''}`}>
      <div className={`Appscreen_IframeContainer app-iframe-container ${isPaidPlan ? 'app-iframe-fullscreen' : ''}`}>
        <iframe
          ref={iframeRef}
          className="Appscreen_Iframe app-iframe"
          sandbox="allow-scripts allow-same-origin allow-forms allow-modals"
          title={app.title || "アプリ"}
        />
      </div>
      {!isPaidPlan && (
        <div className="Appscreen_Info app-info">
        <div className="Appscreen_InfoRow app-info-row">
          <div className="Appscreen_TitleContainer app-title-container">
            <h2 className="Appscreen_Title app-title">{app.title}</h2>
          </div>
          <div className="Appscreen_Actions app-actions">
            {/* いいねボタン - 誰でも使用可能 */}
            <button
              className="Appscreen_LikeButton app-like-button"
              onClick={onLike}
              title={hasLiked ? "いいねを取り消す" : "いいね！"}
              aria-label={hasLiked ? "いいねを取り消す" : "いいね！"}
            >
              <Heart
                size={16}
                className={`${hasLiked ? "heart-icon-filled" : "heart-icon"}`}
              />
              <span className="Appscreen_LikeCount app-like-count">{likes}</span>
            </button>
            <button
              className="Appscreen_ShareButton app-share-button"
              onClick={() => handleShare(app)}
              aria-label="共有する"
            >
              <Share2 size={16} />
            </button>
          </div>
        </div>
        {/* データタイプ情報行 */}
        <div className="Appscreen_DataTypeRow app-data-type-row">
          <span className={`Appscreen_DataTypeInfo app-data-type-info ${app.shared === "true" ? 'Appscreen_DataTypeInfo--shared shared' : 'Appscreen_DataTypeInfo--personal personal'}`}>
            ●  {app.shared === "true" 
              ? "すべての利用者とデータが共有されます" 
              : "データはこのブラウザにのみ保存されます"
            }
          </span>
        </div>

        <div className="Appscreen_InfoSecondaryRow app-info-secondary-row">
          <div className="Appscreen_Author app-author">by {app.displayName || "匿名"}</div>
          <div className="Appscreen_SecondaryActions app-secondary-actions">
            <button 
              className="Appscreen_ApptukuButton app-apptuku-button" 
              onClick={() => {
                window.open('https://lp.aputsuku.com', '_blank');
              }}
            >
              自分だけのアプリをつくろう
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default AppScreen;
